import Icon from "@/components/shared/Icon";
import { logout } from "@/services/permissions.ts";
import { IPublicClientApplication } from "@azure/msal-browser";
import { useLoaderData, useMatch, Link } from "@tanstack/react-router";
import { ComponentPropsWithRef, Ref } from "react";
import { Dropdown } from "react-bootstrap";
import { LuUser, LuBuilding2 } from "react-icons/lu";
import LeaPicker from "./LeaPicker";
import { GoSidebarCollapse, GoSidebarExpand } from "react-icons/go";
import capsdacLogo from "@/public/images/capsdac-logo-2026-star.svg";

/* Typography for LEA + User text */
const headerTextStyle: React.CSSProperties = {
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: "15px",
  lineHeight: "20px",
  fontWeight: 500,
  color: "#0F172A",
};

/* Larger text specifically for LeaPicker */
const leaPickerTextStyle: React.CSSProperties = {
  ...headerTextStyle,
  fontSize: "17px",
  lineHeight: "22px",
};

/* Icon style for LEA + User avatars and chevron */
const headerIconStyle: React.CSSProperties = {
  width: "18px",
  height: "18px",
  color: "#161616",
};

type HeaderProps = {
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
};

export default function Header({
  isSidebarOpen,
  onToggleSidebar,
}: HeaderProps) {
  const globalState = useLoaderData({ from: "__root__" });
  const instance: IPublicClientApplication = globalState.pca;
  const accounts = globalState.pca.getAllAccounts();

  const currentUserOrg = useMatch({ from: "/$leaid", shouldThrow: false })
    ?.context?.currentUserOrg;

  const logoLinkUrl = currentUserOrg ? `/${currentUserOrg.esiIdPath}` : "/";
  const logoLinkLabel =
    "California Department of Education California Preschool Data Collection Home Page";

  return (
    <>
      {/* Skip Link */}
      <a
        href="#content"
        className="visually-hidden-focusable"
        onClick={(e) => {
          // Focus #content directly instead of letting the browser's native
          // hash-navigation scroll the whole (non-scrolling) page shell to
          // bring it to the top, which pushes the fixed header off-screen.
          // A plain focus() call only scrolls the nearest scrollable
          // ancestor (the inner content pane) into view.
          e.preventDefault();
          document.getElementById("content")?.focus();
        }}
      >
        Skip to main content
      </a>

      {/* Non production Banner */}
      {globalState?.env !== "Production" && (
        <div style={{ backgroundColor: "#F00", color: "#fff", padding: "3px" }}>
          NON PRODUCTION ENVIRONMENT: {globalState?.env || "Unknown"}
        </div>
      )}

      <header className="app-header">
        <div className="d-flex align-items-center py-1 ps-3 pe-3">
          {/* LEFT: Collapse toggle + Logo */}
          <div className="d-flex align-items-center gap-3 flex-shrink-0">
            <Link
              to={logoLinkUrl}
              className="d-block flex-shrink-0"
              aria-label={logoLinkLabel}
            >
              <img
                src={capsdacLogo}
                alt=""
                style={{ height: "68px", width: "auto" }}
              />
            </Link>

            <button
              type="button"
              className="header-icon-btn"
              title={isSidebarOpen ? "Collapse Navigation Menu" : "Expand Navigation Menu"}
              aria-label={isSidebarOpen ? "Collapse Navigation Menu" : "Expand Navigation Menu"}
              aria-expanded={isSidebarOpen}
              onClick={onToggleSidebar}
            >
              {isSidebarOpen ? (
                <GoSidebarExpand style={headerIconStyle} />
              ) : (
                <GoSidebarCollapse style={headerIconStyle} />
              )}
            </button>
          </div>

          {/* CENTER: LEA Picker (centered in available space) */}
          <div className="flex-grow-1 d-flex justify-content-center">
            <div
              className="d-flex align-items-center gap-2"
              style={headerTextStyle}
            >
              <LuBuilding2 style={headerIconStyle} />
              <div style={leaPickerTextStyle}>
                <LeaPicker />
              </div>
            </div>
          </div>

          {/* RIGHT: User Dropdown */}
          <div className="d-flex align-items-center gap-4 flex-shrink-0">
            <Dropdown className="header-dropdown" align="end">
              <Dropdown.Toggle as={CustomUserButton}>
                {accounts[0].username}
              </Dropdown.Toggle>

              <Dropdown.Menu
                className="header-dropdown-menu w-100 rounded-4"
                align="end"
              >
                {currentUserOrg ? (
                  <Dropdown.Item
                    as={Link}
                    to={`/${currentUserOrg.esiIdPath}/profile`}
                    className="header-dropdown-item py-2"
                  >
                    Edit Profile
                  </Dropdown.Item>
                ) : (
                  <Dropdown.Item className="header-dropdown-item py-2" disabled>
                    Edit Profile
                  </Dropdown.Item>
                )}

                <Dropdown.Item
                  className="header-dropdown-item py-2"
                  href="https://mysignins.microsoft.com/security-info/password/change"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Change Password
                </Dropdown.Item>

                <div className="dropdown-divider"></div>

                <Dropdown.Item
                  className="header-dropdown-item py-2"
                  onClick={() => logout(instance, accounts[0].homeAccountId)}
                >
                  Sign Out
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </header>
    </>
  );
}

function CustomUserButton({
  children,
  onClick,
  className,
  ref,
  ...rest
}: ComponentPropsWithRef<"button"> & { ref?: Ref<HTMLButtonElement> }) {
  return (
    <button
      id="userProfileMenu"
      ref={ref}
      className="header-user-btn gap-2"
      onClick={(e) => {
        e.preventDefault();
        onClick?.(e as any);
      }}
      {...rest}
      style={{ display: "flex", alignItems: "center" }}
    >
      <span className="user-avatar">
        <LuUser style={headerIconStyle} />
      </span>

      <span className="header-user-email py-2 px-1" style={headerTextStyle}>
        {children}
      </span>

      <span style={{ ...headerIconStyle, display: "inline-flex" }}>
        <Icon name="chevron-down" className="header-user-chevron" />
      </span>
    </button>
  );
}
